import { formatPhoneNumber, PhoneNumberE164, TelecomPhoneLineId } from "@sp-crm/core";
import { useMutation, useQuery } from "@tanstack/react-query";
import { trpc } from "components/rpc/trpc";
import { PhoneInputE164 } from "components/shared/phone-input-e164";
import { InlineBanner } from "components/ui/inline-banner";
import { InternalLink } from "components/ui/internal-link";
import { Radio } from "components/ui/radio";
import { RetryableErrorBanner } from "components/ui/retryable-error-banner";
import { Spinner } from "components/ui/spinner";
import React, { useCallback, useId, useState } from "react";

const personalSettingsPath = "/settings/account/personal";

type ForwardingTarget = "personal" | "another";

interface CallForwardingOptionsProps {
    forwardingPhoneNumber: PhoneNumberE164 | null;
    personalPhoneNumber: PhoneNumberE164 | null;
    onForwardTo: (forwardingPhoneNumber: PhoneNumberE164) => Promise<void>;
    onUsePersonalNumber: () => Promise<void>;
}

const failureMessage = (error: unknown): string =>
    error instanceof Error && error.message
        ? error.message
        : "Unable to save the forwarding number.";

const personalNumberLabel = (personalPhoneNumber: PhoneNumberE164 | null): string =>
    personalPhoneNumber
        ? `Your personal number, ${formatPhoneNumber(personalPhoneNumber)}`
        : "Your personal number";

const MissingPersonalNumberWarning: React.FC = () => (
    <InlineBanner type="warning">
        No personal number is set, so incoming calls can&apos;t connect.{" "}
        <InternalLink to={personalSettingsPath} className="underline">
            Add one in Personal settings
        </InternalLink>
        .
    </InlineBanner>
);

export const CallForwardingOptions: React.FC<CallForwardingOptionsProps> = ({
    forwardingPhoneNumber,
    personalPhoneNumber,
    onForwardTo,
    onUsePersonalNumber,
}) => {
    const radioGroupId = useId();
    const [target, setTarget] = useState<ForwardingTarget>(
        forwardingPhoneNumber ? "another" : "personal",
    );
    const [error, setError] = useState<string | null>(null);
    const [failedSaveCount, setFailedSaveCount] = useState(0);

    const choosePersonalNumber = useCallback(async () => {
        setError(null);
        setTarget("personal");
        try {
            await onUsePersonalNumber();
        } catch (e: unknown) {
            setError(failureMessage(e));
            setTarget("another");
        }
    }, [onUsePersonalNumber]);

    const chooseAnotherNumber = useCallback(() => {
        setError(null);
        setTarget("another");
    }, []);

    const commitForwardingNumber = useCallback(
        async (newForwardingPhoneNumber: PhoneNumberE164) => {
            setError(null);
            try {
                if (newForwardingPhoneNumber) {
                    await onForwardTo(newForwardingPhoneNumber);
                } else {
                    await onUsePersonalNumber();
                    setTarget("personal");
                }
            } catch (e: unknown) {
                setError(failureMessage(e));
                setFailedSaveCount(count => count + 1);
            }
        },
        [onForwardTo, onUsePersonalNumber],
    );

    return (
        <div className="space-y-2">
            <div>Forward incoming calls to</div>
            <div className="flex flex-wrap items-center gap-x-3">
                <div className="flex-none">
                    <Radio
                        name={radioGroupId}
                        checked={target === "personal"}
                        onChange={choosePersonalNumber}
                        label={personalNumberLabel(personalPhoneNumber)}
                    />
                </div>
                {personalPhoneNumber ? (
                    <InternalLink to={personalSettingsPath} className="ml-6 sm:ml-0">
                        View Personal Settings
                    </InternalLink>
                ) : null}
            </div>
            {target === "personal" && !personalPhoneNumber ? (
                <div className="ml-6">
                    <MissingPersonalNumberWarning />
                </div>
            ) : null}
            <Radio
                name={radioGroupId}
                checked={target === "another"}
                onChange={chooseAnotherNumber}
                label="Another number"
            />
            {target === "another" ? (
                <div className="ml-6 max-w-md">
                    <PhoneInputE164
                        key={failedSaveCount}
                        initial={forwardingPhoneNumber}
                        placeholder="Enter a phone number"
                        showButtons={false}
                        onCommit={commitForwardingNumber}
                    />
                </div>
            ) : null}
            {error ? <InlineBanner type="error">{error}</InlineBanner> : null}
        </div>
    );
};

interface CallForwardingSettingProps {
    phoneLineId: TelecomPhoneLineId;
}

export const CallForwardingSetting: React.FC<CallForwardingSettingProps> = ({
    phoneLineId,
}) => {
    const trpcClient = trpc.useTRPC();
    const forwarding = useQuery({
        ...trpcClient.telecom.getCallForwarding.queryOptions({ phoneLineId }),
    });
    const setForwarding = useMutation(
        trpcClient.telecom.setCallForwardingNumber.mutationOptions(),
    );
    const clearForwarding = useMutation(
        trpcClient.telecom.clearCallForwardingNumber.mutationOptions(),
    );

    const forwardTo = useCallback(
        async (forwardingPhoneNumber: PhoneNumberE164) => {
            try {
                await setForwarding.mutateAsync({ phoneLineId, forwardingPhoneNumber });
            } finally {
                await forwarding.refetch();
            }
        },
        [phoneLineId, setForwarding, forwarding],
    );

    const usePersonalNumber = useCallback(async () => {
        try {
            await clearForwarding.mutateAsync({ phoneLineId });
        } finally {
            await forwarding.refetch();
        }
    }, [phoneLineId, clearForwarding, forwarding]);

    if (forwarding.isError) {
        return (
            <RetryableErrorBanner
                message="We couldn't load your call forwarding settings."
                retrying={forwarding.isFetching}
                onRetry={() => {
                    forwarding.refetch().catch((): void => undefined);
                }}
            />
        );
    }
    if (!forwarding.data) {
        return <Spinner />;
    }

    return (
        <CallForwardingOptions
            forwardingPhoneNumber={forwarding.data.forwardingPhoneNumber}
            personalPhoneNumber={forwarding.data.personalPhoneNumber}
            onForwardTo={forwardTo}
            onUsePersonalNumber={usePersonalNumber}
        />
    );
};
